import { useState, useEffect } from 'react'
import { BlogPost } from '../types'

export function useBlogPosts(params?: {
  page?: number
  per_page?: number
  category?: string
  tag?: string
  search?: string
}) {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Fetch from static JSON index
        const response = await fetch('/blog-index.json');
        if (!response.ok) {
          throw new Error('Failed to load blog index');
        }
        
        let allPosts: BlogPost[] = await response.json();
        
        // Apply filters
        if (params?.category) {
          allPosts = allPosts.filter(p => p.category === params.category);
        }
        if (params?.tag) {
          allPosts = allPosts.filter(p => p.tags.includes(params.tag!));
        }
        if (params?.search) {
          const search = params.search.toLowerCase();
          allPosts = allPosts.filter(p => 
            p.title.toLowerCase().includes(search) || 
            p.description.toLowerCase().includes(search)
          );
        }
        
        // Simple pagination
        const perPage = params?.per_page || 10;
        const page = params?.page || 1;
        const start = (page - 1) * perPage;
        const end = start + perPage;
        
        setPosts(allPosts.slice(start, end));
        setHasMore(allPosts.length > end);
        setTotalPages(Math.ceil(allPosts.length / perPage));
        
      } catch (err) {
        setError('An error occurred while fetching posts');
      } finally {
        setLoading(false)
      }
    }

    fetchPosts()
  }, [params?.page, params?.per_page, params?.category, params?.tag, params?.search])

  return { posts, loading, error, hasMore, totalPages }
}

export function useBlogPost(slug: string) {
  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // 1. Find post in index first
        const indexResponse = await fetch('/blog-index.json');
        if (!indexResponse.ok) throw new Error('Index not found');
        const index: BlogPost[] = await indexResponse.json();
        const postMeta = index.find(p => p.slug === slug);
        
        if (!postMeta) {
          setError('Post not found');
          return;
        }
        
        // 2. Load markdown content
        const contentResponse = await fetch(`/content/blog/${slug}.md`);
        if (!contentResponse.ok) throw new Error('Content not found');
        const rawMarkdown = await contentResponse.text();
        
        // 3. Strip frontmatter
        const body = rawMarkdown.replace(/^---\s*\n[\s\S]*?\n---\s*\n/, '');
        
        setPost({
          ...postMeta,
          content: body
        });
        
      } catch (err) {
        setError('An error occurred while fetching the post')
      } finally {
        setLoading(false)
      }
    }

    if (slug) {
      fetchPost()
    }
  }, [slug])

  return { post, loading, error }
}

export function useBlogCategories() {
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true)
        const response = await fetch('/blog-index.json');
        if (response.ok) {
          const index: BlogPost[] = await response.json();
          const uniqueCats = Array.from(new Set(index.map(p => p.category)));
          setCategories(uniqueCats);
        }
      } catch (err) {
        setError('Failed to fetch categories')
      } finally {
        setLoading(false)
      }
    }

    fetchCategories()
  }, [])

  return { categories, loading, error }
}

export function useBlogTags() {
  const [tags, setTags] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoading(true)
        const response = await fetch('/blog-index.json');
        if (response.ok) {
          const index: BlogPost[] = await response.json();
          const allTags = new Set<string>();
          index.forEach(p => p.tags.forEach(t => allTags.add(t)));
          setTags(Array.from(allTags).sort());
        }
      } catch (err) {
        setError('Failed to fetch tags')
      } finally {
        setLoading(false)
      }
    }

    fetchTags()
  }, [])

  return { tags, loading, error }
}
